import { css } from '@adornis/chemistry/directives/css.js';
import type { CampusExercise } from '@chancenwerk/campus-learning-content/entities/CampusExercise.js';
import '@chancenwerk/components/chancen-component-card-title.js';
import { ExerciseType } from '@chancenwerk/db/enums/enums.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import { defineElements, type H5PEditorComponent } from '@lumieducation/h5p-webcomponents';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { map, timer } from 'rxjs';
import { contentService } from '../../globals.js';
import { CampusComponentExerciseEdit } from './campus-page-exercise-edit.js';

defineElements('h5p-editor');

/**
 * @customElement campus-page-exercise-edit-h5p
 */
@customElement('campus-page-exercise-edit-h5p')
export class CampusPageExerciseEditH5P extends CampusComponentExerciseEdit {
  static override exerciseType: number = ExerciseType.H5P;
  static override exerciseName: string = 'H5P-Inhalt';
  static override exerciseElement: string = 'campus-page-exercise-edit-h5p';

  private readonly _editorRef = createRef<H5PEditorComponent>();

  protected override getExcersiseType(): number {
    return CampusPageExerciseEditH5P.exerciseType;
  }

  protected override getExcersieName(): string {
    return CampusPageExerciseEditH5P.exerciseName;
  }

  constructor() {
    super();
  }

  override firstUpdated() {
    timer(100)
      .pipe(
        map(() => {
          if (!this._editorRef.value) {
            return;
          }
          this._editorRef.value.loadContentCallback = (contentId: string) => contentService.getEdit(contentId);
        }),
      )
      .subscribe();

    timer(100)
      .pipe(
        map(() => {
          if (!this._editorRef.value) {
            return;
          }
          this._editorRef.value.saveContentCallback = (contentId: string, body: any) => {
            return contentService.save(
              this.exercise.value.h5pContentID ? this.exercise.value.h5pContentID : undefined,
              body,
            );
          };
        }),
      )
      .subscribe();
  }

  protected override async handleFormSuccess(exercise: CampusExercise): Promise<void> {
    if (!this._editorRef.value) {
      return;
    }

    let result;
    try {
      result = await this._editorRef.value.save();
    } catch (e: any) {
      console.error(e);
      await ChromiumDialog.alert('Beim Speichern ist ein Fehler aufgetreten: ' + e.message);
      return;
    }
    if (!result.contentId) throw new Error('could not save the new h5p content, resolving empty string');

    exercise.type = ExerciseType.H5P;
    exercise.h5pContentID = result.contentId.toString();
    exercise.h5pContentDesc = result.metadata.mainLibrary;
    exercise.h5pContentTitle = result.metadata.extraTitle;
    exercise.title = result.metadata.extraTitle;

    return super.handleFormSuccess(exercise);
  }

  override content() {
    return html`
      ${this.errorMsg.value ? html`<cr-text ${css({ color: 'red' })}>${this.errorMsg.value}</cr-text>` : nothing}
      <cr-card space="md" padding="md" crossaxis-center>
        <cr-text ${css({ minWidth: 'min(960px, 100%)' })}>
          <h5p-editor
            ${ref(this._editorRef)}
            content-id=${this.exercise.value?.h5pContentID?.length ? this.exercise.value.h5pContentID : 'undefined'}
            .onSaved="${() => console.log('editor: on saved')}"
            .onLoaded="${() => console.log('editor: on loaded')}"
            .onSaveError="${() => console.log('editor: on save error')}"
          ></h5p-editor>
        </cr-text>
      </cr-card>
    `;
  }
}
